import { debounce } from '@repo/utils/debounce'
import { ospreyAPI } from '@repo/api/ospreyAPI'
import { storeWrapper } from '../storeWrapper'
import { useEmailCardStore } from '../useEmailCardStore/useEmailCardStore'
import { useToastStore } from '../useToastStore/useToastStore'

const initialState = {
  matterHistoryFolders: []
}

export const useMatterHistoryStore = storeWrapper('useMatterHistoryStore', (set) => ({
  ...initialState,
  fetchAllByMatterHistoryFolderParentId: async ({ matterHistoryFolderParentId, workTypeId }) => {
    return new Promise((resolve) => {
      debounce({
        id: `fetchMatterHistoryFolders-${matterHistoryFolderParentId}`,
        delay: 300,
        callback: async () => {
          const matterHistoryFolders = await ospreyAPI({
            service: 'matters',
            endpoint: '/MatterHistoryFolder/GetAllByMatterHistoryFolderParentId',
            data: {
              matterHistoryFolderParentId: matterHistoryFolderParentId,
              workTypeId: workTypeId
            }
          })

          set({
            matterHistoryFolders: matterHistoryFolders
          })

          resolve(matterHistoryFolders)
        }
      })
    })
  },
  fetchMatterHistoryPracticeDefaults: async ({ resolveParent }) => {
    const { setMatterHistorySettings, setMatterHistorySettingsDefaults } = useEmailCardStore.getState()

    return new Promise((resolve, reject) => {
      debounce({
        id: `fetchPracticeDefaults`,
        delay: 300,
        callback: async () => {
          try {
            const matterHistoryPracticeDefaults = await ospreyAPI({
              method: 'GET',
              service: 'emails',
              endpoint: '/MatterHistoryEmailConfiguration/GetPracticeDefault',
              data: {
                resolveParent: resolveParent
              }
            })

            setMatterHistorySettingsDefaults({ matterHistorySettingsDefaults: matterHistoryPracticeDefaults })
            setMatterHistorySettings({ matterHistorySettings: matterHistoryPracticeDefaults })

            resolve(matterHistoryPracticeDefaults)
          } catch (error) {
            console.error('Error fetching matter history practice defaults:', error)
            useToastStore.getState().showToast({
              type: 'error',
              title: 'Error',
              text: 'Failed to load matter history settings defaults.'
            })
            reject(error)
          }
        }
      })
    })
  },
  fetchMatterHistorySettingsDefaults: async () => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `fetchMatterHistorySettingsDefaults`,
        delay: 300,
        callback: async () => {
          try {
            const matterHistorySettingsDefaults = await ospreyAPI({
              method: 'GET',
              service: 'emails',
              endpoint: '/MatterHistoryEmailConfiguration/GetDefault'
            })

            resolve(matterHistorySettingsDefaults)
          } catch (error) {
            console.error('Error fetching matter history settings defaults:', error)
            useToastStore.getState().showToast({
              type: 'error',
              title: 'Error',
              text: 'Failed to load matter history settings defaults.'
            })
            reject(error)
          }
        }
      })
    })
  },
  createMatterHistoryEmailConfiguration: async ({
    deleteDuplicateImagesAboveCount,
    emailConversationId,
    ignoreInlineImagesBelowKilobytes,
    isIgnoreInlineImages,
    isSaveEmailAttachments,
    isSaveEmailAttachmentsSeparately,
    isSaveEmailBody,
    matterHistoryDetailForReceivedEmail,
    matterHistoryDetailForSentEmail,
    matterHistoryEmailConfigurationId,
    matterHistoryFolderId,
    matterHistoryTypeId,
    retentionDateType,
    retentionPolicyId
  }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `createMatterHistoryEmailConfiguration-${emailConversationId}`,
        delay: 300,
        callback: async () => {
          try {
            const result = await ospreyAPI({
              method: 'POST',
              service: 'emails',
              endpoint: '/MatterHistoryEmailConfiguration/Create',
              data: {
                deleteDuplicateImagesAboveCount,
                emailConversationId,
                ignoreInlineImagesBelowKilobytes,
                isIgnoreInlineImages,
                isSaveEmailAttachments,
                isSaveEmailAttachmentsSeparately,
                isSaveEmailBody,
                matterHistoryDetailForReceivedEmail,
                matterHistoryDetailForSentEmail,
                matterHistoryEmailConfigurationId,
                matterHistoryFolderId,
                matterHistoryTypeId,
                retentionDateType,
                retentionPolicyId
              }
            })

            useToastStore.getState().showToast({
              type: 'success',
              title: 'Saved',
              text: 'Matter history settings defaults have been saved.'
            })

            resolve(result)
          } catch (error) {
            console.error('Error creating matter history email configuration:', error)
            useToastStore.getState().showToast({
              type: 'error',
              title: 'Error',
              text: 'Failed to save matter history settings defaults.'
            })
            reject(error)
          }
        }
      })
    })
  },
  updateMatterHistoryEmailConfiguration: async ({
    deleteDuplicateImagesAboveCount,
    emailConversationId,
    ignoreInlineImagesBelowKilobytes,
    isIgnoreInlineImages,
    isSaveEmailAttachments,
    isSaveEmailAttachmentsSeparately,
    isSaveEmailBody,
    matterHistoryDetailForReceivedEmail,
    matterHistoryDetailForSentEmail,
    matterHistoryEmailConfigurationId,
    matterHistoryFolderId,
    matterHistoryTypeId,
    retentionDateType,
    retentionPolicyId
  }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `updateMatterHistoryEmailConfiguration-${emailConversationId}`,
        delay: 300,
        callback: async () => {
          try {
            const result = await ospreyAPI({
              method: 'PUT',
              service: 'emails',
              endpoint: '/MatterHistoryEmailConfiguration/Update',
              data: {
                deleteDuplicateImagesAboveCount,
                emailConversationId,
                ignoreInlineImagesBelowKilobytes,
                isIgnoreInlineImages,
                isSaveEmailAttachments,
                isSaveEmailAttachmentsSeparately,
                isSaveEmailBody,
                matterHistoryDetailForReceivedEmail,
                matterHistoryDetailForSentEmail,
                matterHistoryEmailConfigurationId,
                matterHistoryFolderId,
                matterHistoryTypeId,
                retentionDateType,
                retentionPolicyId
              }
            })

            useToastStore.getState().showToast({
              type: 'success',
              title: 'Saved',
              text: 'Matter history settings defaults have been saved.'
            })

            resolve(result)
          } catch (error) {
            console.error('Error updating matter history email configuration:', error)
            useToastStore.getState().showToast({
              type: 'error',
              title: 'Error',
              text: 'Failed to save matter history settings defaults.'
            })
            reject(error)
          }
        }
      })
    })
  },
  reset: () => set(initialState)
}))
